import { Audio, AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Platform } from 'react-native';

export interface UseSoundProp {
  url?: string;
}

export interface UseSound {
  status: AVPlaybackStatus;
  sliderValue: number;
  play: () => Promise<AVPlaybackStatus | undefined>;
  pause: () => Promise<AVPlaybackStatus | undefined>;
  stop: () => Promise<AVPlaybackStatus | undefined>;
  togglePlay: () => Promise<AVPlaybackStatus | undefined> | undefined;
  setProgress: (value: number) => void;
  isSlow: boolean;
  isLoaded: boolean;
  duration?: number;
  toggleSlow: () => void;
}

export function useSound({ url }: UseSoundProp): UseSound {
  const soundRef = useRef<Audio.Sound>();
  const [status, setStatus] = useState<AVPlaybackStatus>({
    isLoaded: false,
  });
  const [sliderValue, setSliderValue] = useState(0);
  const [isSlow, setIsSlow] = useState(false);
  const [duration, setDuration] = useState<number>();

  useEffect(() => {
    async function load() {
      if (!url) {
        return;
      }

      try {
        const { sound } = await Audio.Sound.createAsync(
          { uri: url },
          {
            progressUpdateIntervalMillis: 50,
          },
          setStatus
        );
        soundRef.current = sound;
      } catch (error) {
        console.log(error);
      }
    }

    load();

    return function unload() {
      try {
        soundRef.current?.setOnPlaybackStatusUpdate(null);
        soundRef.current?.unloadAsync();
      } catch (error) {
        console.log(error);
      }
    };
  }, [url]);

  useEffect(() => {
    if (duration) {
      return;
    }

    if (status.isLoaded && status.durationMillis) {
      setDuration(status.durationMillis);
    }
  }, [duration, status]);

  // Temp fix for issue with RN Web Expo Audio where duration is NaN
  useEffect(() => {
    if (!status.isLoaded || !soundRef.current || Platform.OS !== 'web') {
      return;
    }

    setTimeout(() => {
      soundRef.current?.getStatusAsync();
    }, 500);
  }, [status.isLoaded]);

  useEffect(() => {
    if (!url) {
      return;
    }

    if (!status || !status?.isLoaded) {
      return;
    }

    setSliderValue(status.positionMillis / (status.durationMillis || 1));

    if (status.didJustFinish) {
      soundRef.current?.stopAsync();
    }
  }, [status, url]);

  useEffect(() => {
    if (!status || !status.isLoaded) {
      return;
    }

    if (isSlow) {
      soundRef.current?.setRateAsync(
        0.8,
        true,
        Audio.PitchCorrectionQuality.High
      );
    } else {
      soundRef.current?.setRateAsync(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlow]);

  function togglePlay() {
    if (!url) {
      return;
    }

    if (!status || !status?.isLoaded) {
      return;
    }

    if (status.isPlaying) {
      return pause();
    } else {
      return play();
    }
  }

  const play = useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync();
    if (!status || !status?.isLoaded) {
      return;
    }

    return soundRef.current?.playAsync();
  }, []);

  const stop = useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync();
    if (!status || !status?.isLoaded || !status?.isPlaying) {
      return;
    }

    return soundRef.current?.stopAsync();
  }, []);

  const pause = useCallback(async () => {
    const status = await soundRef.current?.getStatusAsync();
    if (!status || !status?.isLoaded) {
      return;
    }

    return soundRef.current?.pauseAsync();
  }, []);

  function setProgress(value: number): void {
    if (!url) {
      return;
    }

    if (!soundRef.current || !status?.isLoaded) {
      return;
    }

    const duration = status.durationMillis || 1;
    const newPosition = Math.floor(duration * value);

    setSliderValue(newPosition / duration);
    soundRef.current.setPositionAsync(newPosition);
  }

  function toggleSlow() {
    setIsSlow((value) => !value);
  }

  return {
    status,
    sliderValue,
    play,
    pause,
    stop,
    togglePlay,
    setProgress,
    isSlow,
    isLoaded: status.isLoaded,
    duration,
    toggleSlow,
  };
}
